import { defineStore } from 'pinia';
import * as api from "../services/api";
import jp from "jsonpath";
import route from "../utils/route";
import echoInstance from "../services/echo";
import {t} from "@/i18n";

export const useCampaignStore = defineStore('CampaignStore', {
    state: () => {
        return {
            campaign: {},
            errors: {}, // Key:Value of json_path to errors
            loading: true,
            showSnackbar: false,
            snackbarText: null,
            sessionExpired: false,

            /**
             * Schedule saves need to be serialized.
             *
             * Dragging steps can trigger multiple updateSchedule() calls very quickly.
             * Without this queue, multiple PATCH requests may use the same base_revision.
             */
            scheduleSaveInFlight: false,
            scheduleSaveQueued: false,
            scheduleSaveResolvers: [],
            scheduleSaveTimer: null,
        };
    },

    getters: {
        getErrors: (state) => (path) => state.errors[path]?.errors || [],
        getOriginalValue: (state) => (path) => state.errors[path].value,
    },

    actions: {
        init(campaign) {
            this.campaign = campaign;
        },

        /**
         * patch value in store
         * - Clear stale error messages
         *
         * @param {Object}  data
         * @param {string}  data.json_path
         * @param {*}       data.value
         */
        patchInStore(data) {
            /**
             * Do not overwrite the local schedule while the user is actively saving
             * schedule changes. This can happen through Echo/Broadcast events from
             * our own previous request or from another browser while a drag-save is pending.
             *
             * The actual conflict is still protected by base_revision on the next save.
             */
            const isSchedulePatch = data?.json_path === '$.campaign.schedule'
                || data?.json_path === '$.schedule'
                || data?.field === 'schedule';

            if (
                isSchedulePatch
                && (this.scheduleSaveInFlight || this.scheduleSaveQueued)
            ) {
                return;
            }

            jp.value(this, data.json_path, data.value);
            delete this.errors[data.json_path];

            if (data?.field && Object.prototype.hasOwnProperty.call(data || {}, 'revision')) {
                this.setRevision(data.field, data.revision);
            }

            if (data?.change_revisions) {
                Object.keys(data.change_revisions).forEach(field => {
                    this.setRevision(field, data.change_revisions[field]);
                });
            }
        },

        updateMeta(baseRevision = null) {
            return this.updateField('meta', baseRevision);
        },

        updateComponents(baseRevision = null) {
            return this.updateField('components', baseRevision);
        },

        /**
         * Schedule updates are queued/debounced because drag events can fire
         * multiple saves before the previous request has returned.
         */
        updateSchedule(baseRevision = null) {
            return this.queueScheduleUpdate(baseRevision);
        },

        queueScheduleUpdate(baseRevision = null) {
            return new Promise((resolve, reject) => {
                this.scheduleSaveResolvers.push({resolve, reject});
                this.scheduleSaveQueued = true;

                if (this.scheduleSaveTimer) {
                    window.clearTimeout(this.scheduleSaveTimer);
                }

                this.scheduleSaveTimer = window.setTimeout(() => {
                    this.flushScheduleUpdate(baseRevision);
                }, 150);
            });
        },

        flushScheduleUpdate(baseRevision = null) {
            if (this.scheduleSaveTimer) {
                window.clearTimeout(this.scheduleSaveTimer);
                this.scheduleSaveTimer = null;
            }

            if (this.scheduleSaveInFlight) {
                return;
            }

            if (!this.scheduleSaveQueued) {
                return;
            }

            this.scheduleSaveQueued = false;
            this.scheduleSaveInFlight = true;

            const resolvers = this.scheduleSaveResolvers.splice(0);

            this.updateField('schedule', baseRevision)
                .then(response => {
                    resolvers.forEach(({resolve}) => resolve(response));
                })
                .catch(error => {
                    resolvers.forEach(({reject}) => reject(error));
                })
                .finally(() => {
                    this.scheduleSaveInFlight = false;

                    /**
                     * If another drag/change happened while this request was running,
                     * save the latest local schedule now with the new revision.
                     */
                    if (this.scheduleSaveQueued) {
                        this.flushScheduleUpdate(null);
                    }
                });
        },

        updateField(field, baseRevision = null) {
            return new Promise((resolve, reject) => {
                const url = window.route('campaigns.update', {
                    campaign: this.campaign.id,
                });

                const payload = {
                    field,
                    value: this.campaign[field],
                    base_revision: baseRevision ?? this.getRevision(field),
                };

                api.client.patch(url, payload).then(response => {
                    if (Object.prototype.hasOwnProperty.call(response.data || {}, 'value')) {
                        /**
                         * Important:
                         * If another schedule change was made locally while this request
                         * was in flight, do NOT overwrite the local latest schedule with
                         * the older response payload.
                         */
                        const shouldApplyResponseValue = !(
                            field === 'schedule'
                            && this.scheduleSaveQueued
                        );

                        if (shouldApplyResponseValue) {
                            this.campaign[field] = response.data.value;
                        }
                    }

                    if (Object.prototype.hasOwnProperty.call(response.data || {}, 'revision')) {
                        this.setRevision(field, response.data.revision);
                    }

                    this.snackbarText = t('schedule.campaign_store.saved');
                    this.showSnackbar = true;

                    resolve(response.data);
                }).catch(error => {
                    if (this.handleExpiredEditorSession(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    if (this.handleStaleRevision(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    if (this.responseHasValue(error)) {
                        this.campaign[field] = error.response.data.value;
                    }

                    reject(error.response?.data || error);
                });
            });
        },

        closeSnackbar() {
            this.snackbarText = null;
            this.showSnackbar = false;
        },

        // ----------------------------------------------------------------------------
        // Channel Events
        // ----------------------------------------------------------------------------
        bindChannelEvents(channel_name) {
            echoInstance.join(channel_name)
                .listen('CampaignUpdated', this.patchInStore);
        },

        unbindChannelEvents(channel_name) {
            echoInstance.join(channel_name)
                .stopListening('CampaignUpdated', this.patchInStore);
        },

        // ----------------------------------------------------------------------------
        // Sync with Server
        // ----------------------------------------------------------------------------
        syncWithServer(json_path) {
            // Check if the change is inside the schedule.
            // If so, just run updateSchedule instead of this function
            const parsed = jp.parse(json_path);
            const forSchedule = parsed.findIndex(e => e.expression.value === 'schedule');

            if (forSchedule >= 0) {
                return this.updateSchedule();
            }

            /**
             * Expected Campaign paths:
             * $.campaign.components....
             * $.campaign.meta....
             * $.campaign.legals....
             *
             * CampaignController@update expects:
             * {
             *     field: 'components',
             *     value: this.campaign.components
             * }
             */
            const campaignKey = parsed[1]?.expression?.value;
            const field = parsed[2]?.expression?.value;

            // Die beiden Meldungen hier sind für Entwickler: Ein falscher
            // Pfad ist ein Programmierfehler, und ATextField verwirft sie.
            if (campaignKey !== 'campaign' || !field) {
                return Promise.reject({
                    message: `Ungültiger Campaign JSONPath: ${json_path}`, // i18n-ignore
                    json_path,
                });
            }

            if (typeof this.campaign[field] === 'undefined') {
                return Promise.reject({
                    message: `Das Feld "${field}" konnte in der Kampagne nicht gefunden werden.`, // i18n-ignore
                    field,
                    json_path,
                });
            }

            const payload = {
                field,
                value: this.campaign[field],
                base_revision: this.getRevision(field),
            };

            return new Promise((resolve, reject) => {
                const url = route('campaigns.update', {campaign: this.campaign.id});

                api.client.patch(url, payload).then(response => {
                    // remove existing errors
                    delete this.errors[response.data.json_path];

                    if (Object.prototype.hasOwnProperty.call(response.data || {}, 'revision')) {
                        this.setRevision(field, response.data.revision);
                    }

                    resolve(response.data);
                }).catch(error => {
                    if (this.handleExpiredEditorSession(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    if (this.handleStaleRevision(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    // Save the error Messages
                    const data = error.response?.data;

                    if (data?.json_path) {
                        this.errors[data.json_path] = {
                            value: data.value,
                            errors: data.errors?.[json_path]
                                ? [data.errors[json_path]]
                                : data.errors || [],
                        };
                    } else {
                        this.errors[json_path] = {
                            value: this.campaign[field],
                            errors: data?.errors || data || [t('schedule.campaign_store.save_failed')],
                        };
                    }

                    reject(data || error);
                });
            });
        },

        // ----------------------------------------------------------------------------
        // HELPER FUNCTIONS
        // ----------------------------------------------------------------------------
        responseHasValue(error) {
            return Object.prototype.hasOwnProperty.call(
                error.response?.data || {},
                'value'
            );
        },

        handleExpiredEditorSession(error) {
            const status = error.response?.status;
            const reason = error.response?.data?.reason;

            const sessionExpiredReasons = [
                'session_expired',
                'fresh_session_required',
                'editor_idle_timeout',
            ];

            const method = error.config?.method?.toLowerCase();
            const isMutatingRequest = ['post', 'put', 'patch', 'delete'].includes(method);

            const isNetworkSessionError = !error.response
                && error.code === 'ERR_NETWORK'
                && isMutatingRequest;

            const isExpired = status === 419
                || status === 401
                || sessionExpiredReasons.includes(reason)
                || isNetworkSessionError;

            if (!isExpired) {
                return false;
            }

            if (this.sessionExpired) {
                return true;
            }

            this.sessionExpired = true;

            this.snackbarText = error.response?.data?.message
                || t('schedule.campaign_store.session_expired');
            this.showSnackbar = true;

            window.setTimeout(() => {
                window.location.reload();
            }, 1500);

            return true;
        },

        getRevision(field) {
            return Number(this.campaign.change_revisions?.[field] || 0);
        },

        setRevision(field, revision) {
            if (!this.campaign.change_revisions) {
                this.campaign.change_revisions = {};
            }

            this.campaign.change_revisions[field] = Number(revision || 0);
        },

        handleStaleRevision(error) {
            const status = error.response?.status;
            const reason = error.response?.data?.reason;

            const revisionConflictReasons = [
                'stale_revision',
                'merge_conflict',
            ];

            if (status !== 409 || !revisionConflictReasons.includes(reason)) {
                return false;
            }

            this.snackbarText = error.response?.data?.message
                || t('schedule.campaign_store.stale_revision');
            this.showSnackbar = true;

            window.setTimeout(() => {
                window.location.reload();
            }, 2500);

            return true;
        },
    }
});
